import type { StructuredLogger } from '@mimir/primitive/structuredLogger';
import { Fraction } from '@mimir/common/util/fractionType';
import { AudioPacket, Packet } from './demuxAndDecodeFrameProvider';
import { binarySearch } from '@mimir/common/util/binarySearch';
import { addFractions, compareFractions, frac, subtractFractions } from '@mimir/common/util/integerMath';
import { invariant } from '@mimir/common/util/invariant';

export interface PacketIterator {
  /**
   *  Go to the next packet. If flush is set to false then it will return null
   *  if either of the streams encounter a gap in the cache or the end. If
   *  flush is set to true it will still return null if either stream
   *  encounters a gap, but will continue if a stream encounters the end of the
   *  packet cache.
   */
  next(logger: StructuredLogger, flush: boolean): PacketIterator | null;

  /**
   *  Return the current packet.
   */
  packet(): Packet,

  /**
   *  If more packets have been added to the cache after construction of this
   *  iterator it needs to be refreshed before calling next()
   */
  refresh(): void,
}

export interface PacketCache {
  push(logger: StructuredLogger, packets: Packet[]): void;
  seek(logger: StructuredLogger, time: Fraction): PacketIterator | null;
  count(): number;
}

export function createPacketCache(): PacketCache {
  return createPacketCacheInternal();
}

// ---

function warn(logger: StructuredLogger, message: string, info: any = { }) {
  logger.log({ level: 'warn', prefix: ['PacketCache'], message, ...info });
}

function debug(logger: StructuredLogger, message: string, info: any = { }) {
  logger.log({ level: 'debug', prefix: ['PacketCache'], message, ...info });
}

function createPacketCacheInternal(): PacketCache {
  const packets : Packet[][] = [];

  return {
    push(logger: StructuredLogger, newPackets: Packet[]): void {
      addToPackets(logger, packets, newPackets);
    },
    seek(logger: StructuredLogger, time: Fraction): PacketIterator | null {
      return createIterator(seek(logger, time, packets), packets);
    },
    count(): number {
      return packets.reduce((total, packetsForStream) => total + packetsForStream.length, 0);
    }
  }
}

function addToPackets(
  logger: StructuredLogger,
  target: Packet[][],
  newPackets: Packet[]
) {
  let overwritePacketCount = 0;
  const streamCount = newPackets.reduce((streamCount, packet) => Math.max(streamCount, packet.streamIndex + 1), 0);
  while (streamCount > target.length) {
    target.push([]);
  }

  // TODO: Right now the complexity of this function is O(m log n) where m
  // is the number of packets in the incoming packet and n is the number of
  // packets in the cache. We could implement this further by grouping packets
  // into streams and doing a single binary search lookup to find the gap in
  // which to put the packets. That would reduce the complexity to O(log n).
  for (const packet of newPackets) {
    const packetsForStream = target[packet.streamIndex];
    const index = binarySearch(packetsForStream, packet, compareCacheKey);
    if (index === -1) {
      packetsForStream.push(packet);
    } else if (compareFractions(packetsForStream[index].dts, packet.dts) === 0) {
      overwritePacketCount ++;
      packetsForStream[index] = packet;
    } else {
      packetsForStream.splice(index, 0, packet);
    }
  }

  if (overwritePacketCount > 0) {
    warn(logger, 'Conflicts in packet cache', { overwritePacketCount });
  }
}

function createIterator(
  position: IteratorPosition | null,
  packets: Packet[][],
): PacketIterator | null {
  if (position === null) {
    return null;
  }
  return {
    next(logger: StructuredLogger, flush: boolean): PacketIterator | null {
      return createIterator(nextPosition(logger, position, packets, flush), packets);
    },
    packet(): Packet {
      const streamIndex = getNextStreamIndexOrNull(position.streams);
      invariant(streamIndex !== null, 'Expected stream index');
      const stream = position.streams[streamIndex];
      invariant(stream !== 'end', 'Expected non-ended stream');
      const packetsForStream = packets[streamIndex];
      return packetsForStream[stream.index];
    },
    refresh(): void {
      refreshIterator(position, packets);
    }
  };
}

interface StreamPosition {
  index: number,
  dts: Fraction,
}

interface IteratorPosition {
  streams: (StreamPosition | 'end')[],
}

function seek(
  logger: StructuredLogger,
  time: Fraction,
  packets: Packet[][]
): IteratorPosition | null {
  debug(logger, 'PacketCache.seek()', { time });
  const streams : StreamPosition[] = [];
  for (let streamIndex = 0; streamIndex < packets.length; streamIndex ++) {
    const packetsForStream = packets[streamIndex];
    const index = findPacketIndexAtTimeOrNull(time, packetsForStream);
    if (index === null) {
      debug(logger, 'PacketCache.seek: No packet at requested time for stream', { streamIndex });
      return null;
    }
    debug(logger, 'PacketCache.seek: Finding dependencies for packet', { index, streamIndex });
    const requiredIndex = findRequiredDependentPacketIndexOrNull(logger, index, packetsForStream);
    if (requiredIndex === null) {
      debug(logger, 'PacketCache.seek: Dependencies for packet not available', { index, streamIndex });
      return null;
    }
    streams.push({
      index: requiredIndex,
      dts: packetsForStream[requiredIndex].dts,
    });
  }

  debug(logger, 'PacketCache.seek: positions before dependencies', { streams });

  // Imagine that we seek back 15 frames to the start of the GOP in order to
  // decode the P-frame at the given time. Even though the audio for this
  // frame may only need 1 - 2 packets before the requested time it makes
  // sense that we also decode the audio in continuous run from the starẗ́
  // of the GOP so that we build up complete cache for the GOP instead of
  // partial cache. This way we don't have to re-decode this part of the
  // cache
  const streamIndex = getNextStreamIndexOrNull(streams);
  debug(logger, 'PacketCache.seek: Active stream selected', { streamIndex });
  invariant(streamIndex !== null, 'All stream positions in end state after initialization');
  const lowestDts = streams[streamIndex].dts;
  for (let streamIndex = 0; streamIndex < streams.length; streamIndex ++) {
    while (true) {
      const prevPosition = prevPositionOrNull(streams[streamIndex], packets[streamIndex]);
      if (prevPosition === null) {
        break;
      }
      if (compareFractions(prevPosition.dts, lowestDts) < 0) {
        break;
      }
      const packet = packets[streamIndex][prevPosition.index];
      if (packet.type === 'video' && !packet.keyFrame) {
        // We don't want to move the video position back to a non key frame
        // again.
        break;
      }
      streams[streamIndex] = prevPosition;
    }
  }

  debug(logger, 'PacketCache.seek: positions after dependencies', { streams });
  return { streams };
}

function nextPosition(
  logger: StructuredLogger,
  position: IteratorPosition,
  packets: Packet[][],
  flush: boolean,
): IteratorPosition | null {
  debug(logger, 'nextPosition()', { position, flush });
  let streamIndex = getNextStreamIndexOrNull(position.streams);

  debug(logger, 'nextPosition:', { streamIndex });

  if (streamIndex === null) {
    return null;
  }

  const streamPosition = position.streams[streamIndex];
  invariant(streamPosition !== 'end', 'getNextStreamIndexOrNull returned a null stream');
  const packetsForStream = packets[streamIndex];

  const currentPacketIndex = streamPosition.index;
  const currentPacket = packetsForStream[currentPacketIndex];
  if (compareFractions(currentPacket.dts, streamPosition.dts) !== 0) {
    throw new Error('Stale iterator. Need refresh.');
  }

  const currentPacketEnd = addFractions(currentPacket.dts, currentPacket.duration);

  const nextPacketIndex = currentPacketIndex + 1;
  const nextPacket = packetsForStream[nextPacketIndex];
  if (!nextPacket && flush) {
    // reached end of stream. Remove stream from iterator and retry
    const newPosition = {
      streams: [...position.streams],
    };
    newPosition.streams[streamIndex] = 'end';
    if (getNextStreamIndexOrNull(newPosition.streams) === null) {
      debug(logger, 'nextPosition: reached true end of cache after flush', { currentPacketEnd, streamIndex });
      return null;
    }
    return newPosition;
  }

  if (!nextPacket) {
    debug(logger, 'nextPosition: reached end of current cache', { currentPacketEnd, streamIndex });
    return null;
  }

  if (compareFractions(currentPacketEnd, nextPacket.dts) < 0) {
    debug(logger, 'nextPosition: gap in cache', { currentPacketEnd, nextPacketDts: nextPacket.dts, streamIndex });
    return null;
  }

  const newPosition : IteratorPosition = {
    streams: [...position.streams],
  };

  newPosition.streams[streamIndex] = {
    index: nextPacketIndex,
    dts: nextPacket.dts,
  };

  return newPosition;
}

function refreshIterator(position: IteratorPosition, packets: Packet[][]) {
  for (let streamIndex = 0; streamIndex < position.streams.length; streamIndex ++) {
    const stream = position.streams[streamIndex];
    const packetsForStream = packets[streamIndex];
    if (stream === 'end') {
      throw new Error('Cannot refresh a flushed iterator');
    }
    const packet = packetsForStream[stream.index];
    if (!packet || (compareFractions(stream.dts, packet.dts) !== 0)) {
      const newIndex = binarySearch<CacheKey>(packetsForStream, stream, compareCacheKey);
      const newPacket = packetsForStream[newIndex];
      if (!newPacket || (compareFractions(stream.dts, newPacket.dts) !== 0)) {
        throw new Error('Expired iterator');
      }
      stream.index = newIndex;
    }
  }
}

function getNextStreamIndexOrNull(streams: (StreamPosition | 'end')[]): number | null {
  let streamIndex = -1;
  let low : StreamPosition | null = null;
  for (let i = 0; i < streams.length; i++) {
    const stream = streams[i];
    if (stream === 'end') {
      continue;
    }
    if ((low === null) || (compareFractions(stream.dts, low.dts) < 0)) {
      low = stream;
      streamIndex = i;
    }
  }

  if ((low === null) || (streamIndex === -1)) {
    return null;
  }

  return streamIndex;
}

interface CacheKey {
  dts: Fraction,
}

function getRequiredAudioInitializationFromPacket(packet: AudioPacket): Fraction {
  switch (packet.codec) {
    case 'aac': {
      return subtractFractions(packet.dts, frac(1024, packet.sampleRate));
    }
    default: {
      throw new Error(`Unable to find audio initialization for audio codec: ${packet.codec}`);
    }
  }
}

function prevPositionOrNull(position: StreamPosition, packets: Packet[]): StreamPosition | null {
  const prevIndex = position.index - 1;
  if (prevIndex < 0) {
    // truncated start of cache
    return null;
  }

  const prevPacket = packets[prevIndex];
  const prevPacketEnd = addFractions(prevPacket.dts, prevPacket.duration);
  if (compareFractions(prevPacketEnd, position.dts) < 0) {
    // gap
    return null;
  }

  return {
    index: prevIndex,
    dts: prevPacket.dts,
  };
}

function findRequiredDependentPacketIndexOrNull(
  logger: StructuredLogger,
  requestedIndex: number,
  packets: Packet[]
): number | null {
  let index = requestedIndex;
  let requiredAudioInitialization : Fraction | null = null;

  // TODO: This is time base dependent. We assume the packets originated from Mpeg Ts here
  const epsilon = frac(2, 90000);
  while (true) {
    let packet = packets[index];

    if ((packet.type === 'video') && packet.keyFrame) {
      debug(logger, 'Found video keyframe', { index });
      return index;
    }
    if (packet.type === 'audio') {
      if (requiredAudioInitialization === null) {
        requiredAudioInitialization = getRequiredAudioInitializationFromPacket(packet);
        debug(logger, 'Required audio initialization', { requiredAudioInitialization });
      } else if (compareFractions(subtractFractions(packet.dts, epsilon), requiredAudioInitialization) <= 0) {
        debug(logger, 'Audio packet is sufficient for initialization', { pts: packet.pts });
        return index;
      } else {
        debug(logger, 'Audio packet is insufficient for initialization', { pts: packet.pts });
      }
    }

    index --;
    if (index < 0) {
      return null;
    }
    const prevPacket = packets[index];
    if (prevPacket === undefined) {
      debug(logger, 'Encountered start of cache while looking for dependency');
      return null;
    }
    const prevPacketEnd = addFractions(prevPacket.dts, prevPacket.duration);
    if ((prevPacket === undefined) || (compareFractions(prevPacketEnd, packet.dts) < 0)) {
      debug(logger, 'Encountered gap in cache while looking for dependency.', { prevPacketDts: prevPacket.dts, prevPacketEnd: prevPacketEnd, packetDts: packet.dts });
      return null;
    }
  }
}

function compareCacheKey(a: CacheKey, b: CacheKey): number {
  return compareFractions(a.dts, b.dts);
}

function findPacketIndexAtTimeOrNull(time: Fraction, packets: Packet[]): number | null {
  let index = binarySearch<CacheKey>(packets, { dts: time }, compareCacheKey);
  if (index === -1) {
    index = packets.length - 1;
  }
  if (index === -1) {
    return null;
  }
  let packet = packets[index];
  if (compareFractions(time, packet.dts) < 0) {
    index --;
    packet = packets[index];
  }
  const packetEnd = addFractions(packet.dts, packet.duration);
  if (compareFractions(packetEnd, time) <= 0) {
    return null;
  }
  return index;
}
